import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

interface FieldErrorContextValue {
  registeredFields: Set<string>;
  setRegisteredFields: Dispatch<SetStateAction<Set<string>>>;
}

const fieldErrorContext = createContext<FieldErrorContextValue | null>(null);

export function useFieldErrorContextOrNull(): FieldErrorContextValue | null {
  return useContext(fieldErrorContext);
}

export function useFieldErrorContext(): FieldErrorContextValue {
  const value = useFieldErrorContextOrNull();
  if (!value) {
    throw new Error('useFieldErrorContext must be used in a FieldErrorContextProvider');
  }
  return value;
}

export function FieldErrorContextProvider({ children }: { children: ReactNode }) {
  const [registeredFields, setRegisteredFields] = useState<Set<string>>(new Set());

  const value = useMemo(
    () => ({
      registeredFields,
      setRegisteredFields,
    }),
    [registeredFields],
  );

  return <fieldErrorContext.Provider value={value}>{children}</fieldErrorContext.Provider>;
}

export function useRegisterFieldError(name: string) {
  const setRegisteredFields = useFieldErrorContextOrNull()?.setRegisteredFields;
  useEffect(() => {
    if (setRegisteredFields) {
      setRegisteredFields((prev) => {
        const next = new Set(prev);
        next.add(name);
        return next;
      });

      return () => {
        setRegisteredFields((prev) => {
          const next = new Set(prev);
          next.delete(name);
          return next;
        });
      };
    }

    return undefined;
  }, [name, setRegisteredFields]);
}

export function RegisterFieldError({ name }: { name: string }) {
  useRegisterFieldError(name);
  return null;
}
